import React, { useState, useMemo } from "react";
import "../UIcss/Inputs.css";
import Pagination from "./Pagination.jsx";
import CodeViewer from "./CodeViewer.jsx";
import { FaKeyboard } from "react-icons/fa";
import { LuCopy } from "react-icons/lu";
import { inputStylesData } from "./inputStylesData.js";

// --- RENDERER COMPONENT ---

const InputRenderer = ({ item, index, onShowCode }) => {
  return (
    <div className="input-card" onClick={() => onShowCode(item)}>
      <div className="input-meta-header">
        <span className="input-label">{index + 1}. {item.name}</span>
        <button 
          className="input-action-btn" 
          onClick={(e) => {
            e.stopPropagation();
            onShowCode(item);
          }}
          title="View Code"
        >
          <LuCopy />
        </button>
      </div>

      <div className="input-preview-area">
        <div className="input-main-preview">
          {item.isGradient ? (
            <div className="input-style-5-wrapper" style={{ width: "100%" }}>
              <input
                type={item.typeAttr}
                className={item.className}
                placeholder={item.typeName}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <input
              type={item.typeAttr}
              className={`${item.className} input-base`}
              placeholder={item.typeName}
              style={{ width: "100%" }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </div>
      
      <div className="input-details-footer">
        <div className="input-desc-text">{item.typeName} Style</div>
      </div>
    </div>
  );
};

const Inputs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  
  const [viewerState, setViewerState] = useState({
    isOpen: false,
    name: "",
    jsx: "",
    css: "",
  });

  const inputTypes = [
    { type: "text", label: "Text Field" },
    { type: "password", label: "Password" },
    { type: "email", label: "Email" },
    { type: "number", label: "Number" },
    { type: "search", label: "Search" },
    { type: "date", label: "Date" },
    { type: "time", label: "Time" },
    { type: "tel", label: "Phone" },
    { type: "url", label: "URL" },
    { type: "color", label: "Color Picker" },
  ];

  const styles = [
    { id: 1, name: "Indigo Glow", class: "input-style-1" },
    { id: 2, name: "Sleek Underline", class: "input-style-2" },
    { id: 3, name: "Dark Neo", class: "input-style-3" },
    { id: 4, name: "Glass Frost", class: "input-style-4" },
    { id: 5, name: "Vibrant Gradient", class: "input-style-5" },
    { id: 6, name: "Retro Terminal", class: "input-style-6" },
    { id: 7, name: "Minimal Pill", class: "input-style-7" },
    { id: 8, name: "Cyberpunk", class: "input-style-8" },
    { id: 9, name: "Rose Accent", class: "input-style-9" },
    { id: 10, name: "Material Dark", class: "input-style-10" },
    { id: 11, name: "Electric Cyan", class: "input-style-11" },
    { id: 12, name: "High Contrast", class: "input-style-12" },
    { id: 13, name: "Dashed Border", class: "input-style-13" },
    { id: 14, name: "Left Accent", class: "input-style-14" },
    { id: 15, name: "Inset Shadow", class: "input-style-15" },
    { id: 16, name: "Matrix Code", class: "input-style-16" },
    { id: 17, name: "Pop Art", class: "input-style-17" },
    { id: 18, name: "Hairline", class: "input-style-18" },
    { id: 19, name: "Bold Edge", class: "input-style-19" },
    { id: 20, name: "Prismatic", class: "input-style-20" },
  ];

  const allItems = useMemo(() => {
    const items = [];
    let idCounter = 1;
    styles.forEach((style) => {
      inputTypes.forEach((type) => {
        const isGradient = style.id === 5;
        const className = isGradient ? "input-style-5" : style.class;
        
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${style.name} - CodePulse UI</title>
    <!-- Include Google Font -->
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
    <style>
        body { 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            min-height: 100vh; 
            margin: 0; 
            font-family: 'Inter', sans-serif; 
        }
        .container {
            width: 100%;
            max-width: 400px;
            padding: 20px;
        }
        /* Paste the CSS from the CSS tab here */
    </style>
</head>
<body>
    <div class="container">
        ${isGradient 
          ? `<div class="input-style-5-wrapper">\n            <input type="${type.type}" class="${className}" placeholder="${style.name} - ${type.label}">\n        </div>`
          : `<input type="${type.type}" class="${className} input-base" placeholder="${style.name} - ${type.label}">`
        }
    </div>
</body>
</html>`;

        const baseStyle = inputStylesData[".input-base"] || "";
        const specificStyle = inputStylesData[`.${style.class}`] || "";
        const focusStyle = inputStylesData[`.${style.class}:focus`] || "";
        const wrapperStyle = isGradient ? inputStylesData[".input-style-5-wrapper"] || "" : "";

        let css = `/* Base Style */\n.input-base {\n  ${baseStyle}\n}\n\n`;
        if (isGradient) css += `/* Gradient Wrapper */\n.input-style-5-wrapper {\n  ${wrapperStyle}\n}\n\n`;
        css += `/* Specific Style: ${style.name} */\n.${style.class} {\n  ${specificStyle}\n}`;
        if (focusStyle) css += `\n\n.${style.class}:focus {\n  ${focusStyle}\n}`;

        items.push({
          id: idCounter++,
          name: `${style.name} (${type.label})`,
          typeName: type.label,
          typeAttr: type.type,
          className,
          isGradient,
          jsx: html, // Display complete HTML boilerplate
          css: css,
        });
      });
    });
    return items;
  }, [inputStylesData]);

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const currentItems = useMemo(() => {
    return allItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);
  }, [indexOfFirstItem, allItems]);
  
  const totalPages = Math.ceil(allItems.length / itemsPerPage);

  const handleShowCode = (item) => {
    setViewerState({
      isOpen: true,
      name: item.name,
      jsx: item.jsx,
      css: item.css,
    });
  };

  return (
    <div className="inputs-page-wrapper">
      <div className="library-header">
        <div className="header-content">
          <h1><FaKeyboard /> Inputs <span>Library</span></h1>
        </div>
      </div>

      <div className="input-grid">
        {currentItems.map((item, index) => (
          <InputRenderer 
            key={item.id} 
            item={item} 
            index={indexOfFirstItem + index}
            onShowCode={handleShowCode} 
          />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={allItems.length}
        itemsPerPage={itemsPerPage}
      />

      <CodeViewer
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        componentName={viewerState.name}
        jsxCode={viewerState.jsx}
        cssCode={viewerState.css}
      />
    </div>
  );
};

export default Inputs;
